import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import {
    deleteEcosystemService,
    getAdminEcosystemServices,
} from 'apis/adminEcosystemServices';
import { getAdminEcosystemGroups } from 'apis/adminEcosystemGroups';
import {
    useActiveLanguagesQuery,
    useAdminDeleteMutation,
    useAdminListQuery,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const List = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [keyword, setKeyword] = useState('');
    const [ecosystemGroupIdFilter, setEcosystemGroupIdFilter] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const queryParams = useMemo(
        () => ({
            pageIndex: currentPage,
            pageSize,
            keyword: keyword || undefined,
            ecosystemGroupId: ecosystemGroupIdFilter || undefined,
            languageCode: languageCodeFilter || undefined,
        }),
        [currentPage, pageSize, keyword, ecosystemGroupIdFilter, languageCodeFilter],
    );

    const {
        data = { items: [], total: 0, currentPage, pageSize },
        isFetching,
        error,
    } = useAdminListQuery('ecosystem-services', queryParams, getAdminEcosystemServices);

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: groupsData = { items: [] } } = useAdminListQuery(
        'ecosystem-groups-options',
        { pageSize: 100, languageCode: 'vi' },
        getAdminEcosystemGroups,
    );
    const groups = groupsData.items || [];

    const deleteMutation = useAdminDeleteMutation(
        'ecosystem-services',
        deleteEcosystemService,
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [keyword, ecosystemGroupIdFilter, languageCodeFilter]);

    useEffect(() => {
        if (error) toast.error(error.message || 'Tải dữ liệu thất bại');
    }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res.success) toast.success('Xóa thành công');
            else toast.error(res.message);
        } catch {
            toast.error('Xóa thất bại');
        }
        setDeleteId(null);
    };

    const groupMap = useMemo(() => {
        const map = {};
        const list = Array.isArray(groupsData?.items) ? groupsData.items : (Array.isArray(groupsData) ? groupsData : []);
        list.forEach((g) => {
            const id = g.id || g.Id;
            if (id) {
                map[id] = g.name || g.code;
                map[String(id)] = g.name || g.code;
                map[String(id).toLowerCase()] = g.name || g.code;
            }
        });
        return map;
    }, [groupsData]);

    const groupFilterOptions = [
        { value: '', label: 'Tất cả nhóm hệ sinh thái' },
        ...groups.map((g) => ({
            value: g.id,
            label: g.name || g.code,
        })),
    ];

    const columns = [
        {
            field: 'title',
            header: 'Tên dịch vụ',
            render: (row) => (
                <div>
                    <div style={{ fontWeight: 600, color: '#14291a', fontSize: 14 }}>{row.title || '---'}</div>
                    {row.description && row.description !== row.title && (
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            {row.description}
                        </div>
                    )}
                </div>
            ),
        },
        {
            field: 'ecosystemGroupId',
            header: 'Nhóm hệ sinh thái',
            render: (row) => {
                const groupId = row.ecosystemGroupId || row.EcosystemGroupId;
                const groupName = groupId ? (groupMap[groupId] || groupMap[String(groupId).toLowerCase()] || '---') : '---';
                return (
                    <span className="badge badge--info">
                        {groupName}
                    </span>
                );
            },
        },
        {
            field: 'sortOrder',
            header: 'Thứ tự',
            render: (row) => <span className="badge badge--default">{row.sortOrder ?? 0}</span>,
        },
        {
            field: 'isActive',
            header: 'Trạng thái',
            render: (row) => (
                <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>
                    {row.isActive ? 'Hoạt động' : 'Tạm ẩn'}
                </span>
            ),
        },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button
                        className="icon-btn icon-btn--edit"
                        onClick={() => navigate(`/admin/ecosystem-services/edit/${row.id}`)}
                        title="Chỉnh sửa"
                    >
                        <FiEdit2 />
                    </button>
                    <button
                        className="icon-btn icon-btn--delete"
                        onClick={() => setDeleteId(row.id)}
                        title="Xóa"
                    >
                        <FiTrash2 />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Quản lý Dịch vụ Hệ sinh thái"
                subtitle="Quản lý 31 dịch vụ chi tiết của 7 lĩnh vực hệ sinh thái HSV"
                action={
                    <Button onClick={() => navigate('/admin/ecosystem-services/create')}>
                        + Thêm Dịch vụ
                    </Button>
                }
            />
            <div className="filters-bar">
                <div className="filter-item">
                    <Input
                        placeholder="Tìm theo tên dịch vụ..."
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={ecosystemGroupIdFilter}
                        onChange={(e) => setEcosystemGroupIdFilter(e.target.value)}
                        options={groupFilterOptions}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={languageCodeFilter}
                        onChange={(e) => setLanguageCodeFilter(e.target.value)}
                        options={languages.map((l) => ({ value: l.code, label: l.name }))}
                    />
                </div>
            </div>
            <Table
                columns={columns}
                data={data.items || []}
                loading={isFetching}
                pagination={{
                    currentPage,
                    totalPages: Math.ceil((data.total || 0) / pageSize) || 1,
                    onPageChange: setCurrentPage,
                }}
            />
            <ConfirmationDialog
                isOpen={!!deleteId}
                title="Xác nhận xóa"
                message="Bạn có chắc chắn muốn xóa dịch vụ hệ sinh thái này?"
                onConfirm={handleDelete}
                onCancel={() => setDeleteId(null)}
            />
        </div>
    );
};

export default List;
